import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { REGIONS } from '../lib/regions';
import axios from 'axios';
import { 
  Clock, 
  Users, 
  HandHeart, 
  MessageSquare, 
  ArrowRight,
  TrendingUp,
  Star,
  Calendar,
  Plus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const [recentOffers, setRecentOffers] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [myExchanges, setMyExchanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [offersRes, requestsRes, exchangesRes] = await Promise.all([
          axios.get(`${API_URL}/api/offers?status=active`, { withCredentials: true }),
          axios.get(`${API_URL}/api/requests?status=open`, { withCredentials: true }),
          axios.get(`${API_URL}/api/exchanges`, { withCredentials: true })
        ]);
        setRecentOffers(offersRes.data.slice(0, 3));
        setRecentRequests(requestsRes.data.slice(0, 3));
        setMyExchanges(exchangesRes.data.slice(0, 5));
        refreshUser();
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshUser]);

  const stats = [
    {
      label: 'Time Credits',
      value: user?.time_credits?.toFixed(1) || '0.0',
      icon: Clock,
      color: 'text-[#090000]',
      bgColor: 'bg-[#ffd703]/20'
    },
    {
      label: 'Active Offers',
      value: recentOffers.filter(o => o.user_id === user?.user_id).length.toString(),
      icon: HandHeart,
      color: 'text-[#090000]',
      bgColor: 'bg-[#c7dfe9]/50'
    },
    {
      label: 'Open Requests',
      value: recentRequests.filter(r => r.user_id === user?.user_id).length.toString(),
      icon: MessageSquare,
      color: 'text-[#090000]',
      bgColor: 'bg-[#d1c7e9]/50'
    },
    {
      label: 'Completed Exchanges',
      value: myExchanges.filter(e => e.status === 'completed').length.toString(),
      icon: TrendingUp,
      color: 'text-[#090000]',
      bgColor: 'bg-[#ffd703]/30'
    }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[#ffd703]/20 text-[#090000]',
      confirmed: 'bg-[#c7dfe9]/50 text-[#090000]',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.pending;
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      low: 'bg-[#c7dfe9]/50 text-[#090000]',
      medium: 'bg-[#ffd703]/30 text-[#090000]',
      high: 'bg-red-100 text-red-800'
    };
    return styles[urgency] || styles.low;
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#090000] mb-2">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#090000]/60">
            Here's what's happening in your time banking community.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => (
            <Card key={stat.label} className="" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold text-[#090000]">{stat.value}</p>
                    <p className="text-xs text-[#090000]/60">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <Link to="/community?tab=offers&action=new">
            <Card className="hover: transition-all cursor-pointer group" data-testid="quick-offer-skill">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#ffd703]/20 flex items-center justify-center group-hover:bg-[#ffd703]/30 transition-colors">
                  <HandHeart className="w-6 h-6 text-[#090000]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#090000] group-hover:text-[#090000] transition-colors">
                    Offer a Skill
                  </h3>
                  <p className="text-sm text-[#090000]/60">Share your expertise with the community</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#090000]/40 group-hover:text-[#090000] transition-colors" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/community?tab=requests&action=new">
            <Card className="hover: transition-all cursor-pointer group" data-testid="quick-request-help">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#d1c7e9]/50 flex items-center justify-center group-hover:bg-[#d1c7e9]/70 transition-colors">
                  <MessageSquare className="w-6 h-6 text-[#090000]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#090000] group-hover:text-[#090000] transition-colors">
                    Request Help
                  </h3>
                  <p className="text-sm text-[#090000]/60">Ask for support from community members</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#090000]/40 group-hover:text-[#090000] transition-colors" />
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Regional Dashboards (St. Louis) */}
        <Card className="mb-8" data-testid="regional-dashboards">
          <CardHeader>
            <CardTitle className="text-lg font-bold text-black font-sans">St. Louis Dashboards</CardTitle>
            <p className="text-sm text-black/60 font-sans">Browse trades by neighborhood / region.</p>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {REGIONS.map(r => (
                <Link
                  key={r.value}
                  to={`/community?region=${r.value}`}
                  className={`border-[3px] border-black rounded-xl p-4 font-sans text-center transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#000] ${user?.home_region === r.value ? 'bg-[#ffd703] shadow-[6px_6px_0px_#000]' : 'bg-[#fef9e7] shadow-[4px_4px_0px_#000]'}`}
                  data-testid={`dashboard-region-${r.value}`}
                >
                  <p className="font-bold text-black text-sm">{r.label}</p>
                  {user?.home_region === r.value && (
                    <p className="text-xs text-black/70 mt-1">★ your home</p>
                  )}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Skill Offers */}
          <Card className="">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium text-[#090000]">Recent Skill Offers</CardTitle>
              <Link to="/community?tab=offers" className="text-sm text-[#090000]/70 hover:text-[#090000] hover:underline">
                View all
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="text-center py-4 text-[#090000]/60">Loading...</div>
              ) : recentOffers.length === 0 ? (
                <div className="text-center py-4 text-[#090000]/60">No offers yet</div>
              ) : (
                recentOffers.map((offer) => (
                  <div key={offer.offer_id} className="p-3 bg-[#d1c7e9]/20 rounded-lg" data-testid={`offer-${offer.offer_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-[#090000] text-sm">{offer.title}</h4>
                      <Badge className="bg-[#ffd703]/30 text-[#090000] text-xs">
                        {offer.estimated_hours}h
                      </Badge>
                    </div>
                    <p className="text-xs text-[#090000]/60 mb-2 line-clamp-2">{offer.description}</p>
                    <div className="flex items-center gap-2 text-xs text-[#090000]/60">
                      <Users className="w-3 h-3" />
                      {offer.user_name}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Recent Support Requests */}
          <Card className="">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium text-[#090000]">Help Needed</CardTitle>
              <Link to="/community?tab=requests" className="text-sm text-[#090000]/70 hover:text-[#090000] hover:underline">
                View all
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="text-center py-4 text-[#090000]/60">Loading...</div>
              ) : recentRequests.length === 0 ? (
                <div className="text-center py-4 text-[#090000]/60">No requests yet</div>
              ) : (
                recentRequests.map((request) => (
                  <div key={request.request_id} className="p-3 bg-[#c7dfe9]/30 rounded-lg" data-testid={`request-${request.request_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-[#090000] text-sm">{request.title}</h4>
                      <Badge className={getUrgencyBadge(request.urgency)}>
                        {request.urgency}
                      </Badge>
                    </div>
                    <p className="text-xs text-[#090000]/60 mb-2 line-clamp-2">{request.description}</p>
                    <div className="flex items-center gap-2 text-xs text-[#090000]/60">
                      <Users className="w-3 h-3" />
                      {request.user_name}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* My Recent Exchanges */}
          <Card className="">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-medium text-[#090000]">My Exchanges</CardTitle>
              <Link to="/exchanges" className="text-sm text-[#090000]/70 hover:text-[#090000] hover:underline">
                View all
              </Link>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading ? (
                <div className="text-center py-4 text-[#090000]/60">Loading...</div>
              ) : myExchanges.length === 0 ? (
                <div className="text-center py-8 text-[#090000]/60">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No exchanges yet</p>
                  <Link to="/community">
                    <Button size="sm" variant="outline" className="mt-3">
                      <Plus className="w-4 h-4 mr-1" />
                      Start Exchanging
                    </Button>
                  </Link>
                </div>
              ) : (
                myExchanges.map((exchange) => (
                  <div key={exchange.exchange_id} className="p-3 bg-[#ffd703]/10 rounded-lg" data-testid={`exchange-${exchange.exchange_id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#090000]/60">
                          {exchange.initiator_id === user?.user_id ? 'You' : exchange.initiator_name}
                          {' → '}
                          {exchange.partner_id === user?.user_id ? 'You' : exchange.partner_name}
                        </span>
                      </div>
                      <Badge className={getStatusBadge(exchange.status)}>
                        {exchange.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#090000] font-medium mb-1">{exchange.description}</p>
                    <div className="flex items-center gap-3 text-xs text-[#090000]/60">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {exchange.hours}h
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(exchange.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Profile Completion Prompt */}
        {(!user?.skills || user?.skills.length === 0) && (
          <Card className="mt-8 border-[#ffd703] bg-[#ffd703]/10">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#ffd703]/30 flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-[#090000]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#090000] mb-1">Complete Your Profile</h3>
                  <p className="text-sm text-[#090000]/60 mb-3">
                    Add your skills to let others know what you can offer to the community.
                  </p>
                  <Link to="/profile">
                    <Button size="sm" className="bg-[#ffd703] hover:bg-[#e6c203] text-[#090000]" data-testid="complete-profile-btn">
                      Update Profile
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
